import React from 'react'
import { Form, Input, Modal, Radio } from 'antd'
import { actionsApi } from 'api/actions'
import { IAdLink, ICreateAdLink, TAdLinkType } from 'models/adLinks'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import {
  AD_LINK_CODE_MAX_LENGTH,
  AD_LINK_CODE_REGEX,
  adLinkTypeLabels,
  codeFromName
} from './adLinkTypes'

const NAME_MAX_LENGTH = 100

const typeOptions = (Object.keys(adLinkTypeLabels) as TAdLinkType[]).map((type) => ({
  value: type,
  label: adLinkTypeLabels[type]
}))

interface IAdLinkModalProps {
  open: boolean
  // null — новая ссылка
  link: IAdLink | null
  onClose: () => void
  onSaved: (link: IAdLink) => void
}

export const AdLinkModal: React.FC<IAdLinkModalProps> = ({
  open,
  link,
  onClose,
  onSaved
}) => {
  const [form] = Form.useForm<ICreateAdLink>()
  const [isSaving, setIsSaving] = React.useState(false)
  // Пока код не правили руками, он собирается из названия
  const [isCodeEdited, setIsCodeEdited] = React.useState(false)
  const path: string | undefined = Form.useWatch('path', form)
  const isPathChanged = Boolean(link) && (path ?? '').trim() !== link?.path

  React.useEffect(() => {
    if (!open) return
    form.resetFields()
    setIsCodeEdited(false)
    if (link) {
      const { name, type, code, path } = link
      form.setFieldsValue({ name, type, code, path })
    }
  }, [open, link, form])

  const onValuesChange = (changed: Partial<ICreateAdLink>) => {
    if ('code' in changed) setIsCodeEdited(true)
    if ('name' in changed && !link && !isCodeEdited) {
      form.setFieldValue('code', codeFromName(changed.name ?? ''))
    }
  }

  const onSubmit = async () => {
    let values: ICreateAdLink
    try {
      values = await form.validateFields()
    } catch {
      // ошибки уже показаны под полями
      return
    }

    setIsSaving(true)
    try {
      const { data } = link
        ? await actionsApi.updateAdLink(link._id, {
          name: values.name.trim(),
          path: values.path?.trim() ?? ''
        })
        : await actionsApi.createAdLink({
          ...values,
          name: values.name.trim(),
          path: values.path?.trim() ?? ''
        })
      showMessage('success', link ? 'Изменения сохранены' : 'Ссылка создана', '')
      onSaved(data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      title={link ? 'Рекламная ссылка' : 'Новая рекламная ссылка'}
      okText={link ? 'Сохранить' : 'Создать'}
      cancelText='Отмена'
      confirmLoading={isSaving}
      onOk={onSubmit}
      onCancel={onClose}
      destroyOnClose
    >
      <Form
        form={form}
        layout='vertical'
        initialValues={{ type: 'blogger', path: '/' }}
        onValuesChange={onValuesChange}
        requiredMark={false}
      >
        <Form.Item
          name='type'
          label='Тип'
          extra={link ? 'Тип уже в выданной ссылке и не меняется' : undefined}
        >
          <Radio.Group
            options={typeOptions}
            optionType='button'
            disabled={Boolean(link)}
          />
        </Form.Item>
        <Form.Item
          name='name'
          label='Название'
          extra='В аналитике источник будет называться «Тип (название)»'
          rules={[{ required: true, whitespace: true, message: 'Укажите название' }]}
        >
          <Input
            maxLength={NAME_MAX_LENGTH}
            showCount
            placeholder='Например, «Анна Иванова — Instagram»'
          />
        </Form.Item>
        <Form.Item
          name='code'
          label='Код в ссылке'
          extra={
            link
              ? 'Код уже в выданной ссылке и не меняется'
              : 'Латиница и цифры через дефис. Виден в ссылке как utm_source'
          }
          rules={[
            { required: true, message: 'Укажите код' },
            {
              pattern: AD_LINK_CODE_REGEX,
              message: 'Только строчная латиница и цифры через дефис или подчёркивание'
            }
          ]}
          normalize={(value: string) => value.toLowerCase()}
        >
          <Input maxLength={AD_LINK_CODE_MAX_LENGTH} disabled={Boolean(link)} />
        </Form.Item>
        <Form.Item
          name='path'
          label='Страница сайта'
          extra={
            isPathChanged
              ? 'Ссылка изменится. Уже выданная продолжит работать и засчитываться'
              : 'Путь от корня или полный адрес страницы horsesmart.store. / — главная'
          }
        >
          <Input placeholder='/category/... или https://horsesmart.store/...' />
        </Form.Item>
      </Form>
    </Modal>
  )
}
